/**
 * The avatar catalog: the faces a player may wear.
 *
 * The same keys as `Player::AvatarCatalog`, which is the authority. A key is
 * what `me.avatar_id` carries, never an image location; this module is where a
 * key becomes art the client ships. Each face has a still under
 * `public/avatars/<id>.webp` (the approved neutral, which every head draws) and
 * its rig layers under `public/avatars/<id>/`. Names are i18n, under
 * `avatars.<id>`, and are the cast's names (`docs/dev/avatars.md`, Catalog).
 *
 * Four faces are free and every other face needs Communications Pro
 * (`me.entitlements.comms`). Change avatar lists them all, the free four
 * first; a free player can look at a member's face but not save it.
 */
export const AVATAR_IDS = [
  'scout',
  'whiz',
  'pixel',
  'grind',
  'champ',
  'vibe',
  'spike',
  'star',
  'diva',
  'flex',
  'timber',
  'mime',
  'suit',
  'major',
  'gala',
  'chef',
  'patch',
  'sleuth',
  'coach',
  'tank',
  'sarge',
  'zora',
  'duke',
  'hay',
  'doc',
  'skipper',
  'moonbeam',
  'gigi',
  'nana',
  'pops',
  'skunk',
  'fox',
  'magpie',
  'owl',
  'crab',
  'hare',
  'cougar',
  'dog',
  'cat',
  'dragon',
  'horse',
  'unicorn',
  'mouse',
] as const;

export type AvatarId = (typeof AVATAR_IDS)[number];

/**
 * The faces every player gets without a subscription, in the order a table
 * hands them out. Animals, because an animal shows no age, gender or race.
 * Every face's still is precached (`vite.config.ts`), since any of them can
 * be a human seat's head.
 */
export const FREE_AVATARS: readonly AvatarId[] = ['fox', 'owl', 'dog', 'cat'];

export function isAvatarId(value: unknown): value is AvatarId {
  return (AVATAR_IDS as readonly unknown[]).includes(value);
}

/** The face's still: the approved neutral every head draws. */
export function avatarSrc(id: AvatarId): string {
  return `/avatars/${id}.webp`;
}

/** The folder of the face's rig layers, for `AvatarRig`'s `dir`. */
export function avatarRigDir(id: AvatarId): string {
  return `/avatars/${id}`;
}

/**
 * The face a player chose, from `/me`'s `avatar_id`. The Fox before the
 * server has answered, and for a key this build has no art for.
 */
export function ownFace(avatarId: string | null | undefined): AvatarId {
  return isAvatarId(avatarId) ? avatarId : 'fox';
}
